import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { EditableNumber } from './EditableNumber';
interface OutputEstimatorProps {
  adSpend: number;
  cpaLow: number;
  cpaHigh: number;
  onAdSpendChange: (next: number) => void;
  onCpaLowChange: (next: number) => void;
  onCpaHighChange: (next: number) => void;
}
const fmt = (value: number) => value.toLocaleString('en-US');
export function OutputEstimator({
  adSpend,
  cpaLow,
  cpaHigh,
  onAdSpendChange,
  onCpaLowChange,
  onCpaHighChange
}: OutputEstimatorProps) {
  const [isEditingTarget, setIsEditingTarget] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const midCpa = (cpaLow + cpaHigh) / 2;
  const rangeLow = Math.max(1, Math.round(adSpend / cpaHigh));
  const rangeHigh = Math.max(rangeLow + 1, Math.round(adSpend / cpaLow));
  useEffect(() => {
    if (isEditingTarget) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditingTarget]);
  const startEditingTarget = () => {
    setDraft(String(Math.round(adSpend / midCpa)));
    setIsEditingTarget(true);
  };
  const commitTarget = () => {
    const parsed = Number(draft.replace(/[^0-9]/g, ''));
    if (Number.isFinite(parsed) && parsed > 0) {
      const hires = Math.min(500, Math.max(1, parsed));
      onAdSpendChange(Math.round(hires * midCpa));
    }
    setIsEditingTarget(false);
  };
  const stepSpend = (direction: -1 | 1) => {
    const next = direction === 1 ? Math.floor(adSpend / 2500) * 2500 + 2500 : Math.ceil(adSpend / 2500) * 2500 - 2500;
    onAdSpendChange(Math.min(250000, Math.max(2500, next)));
  };
  const arrowButton = 'w-11 h-11 shrink-0 rounded-full flex items-center justify-center text-[#7FE0C3] opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity duration-150 ease-out hover:bg-[#7FE0C3]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]';
  const decreaseButton = <button type="button" onClick={() => stepSpend(-1)} aria-label="Decrease monthly ad spend by $2,500" className={arrowButton} data-id="element-213" data-mp="1i69ni51sskb3">
      <ChevronLeftIcon className="w-6 h-6" data-id="element-214" data-mp="18827ppj8w8ao" />
    </button>;
  const increaseButton = <button type="button" onClick={() => stepSpend(1)} aria-label="Increase monthly ad spend by $2,500" className={arrowButton} data-id="element-215" data-mp="1d6dhk09jiisw">
      <ChevronRightIcon className="w-6 h-6" data-id="element-216" data-mp="zdxmblmvrp2j" />
    </button>;
  return <div className="relative group w-full" data-id="element-217" data-mp="1470gnvzhf2js">
      <div className="rounded-2xl border border-[#7FE0C3]/40 bg-[#7FE0C3]/5 px-8 md:px-20 py-16 md:py-24 text-center" data-id="element-218" data-mp="147s4ulqr1y01">
        <div className="text-sm font-bold uppercase tracking-widest text-[#7FE0C3] mb-8" data-id="element-219" data-mp="ufw390pgfpr2">
          Average Monthly Output
        </div>

        {isEditingTarget ? <div className="flex items-center justify-center gap-4" data-id="element-220" data-mp="gih9jjs64vta">
            {decreaseButton}
            <input ref={inputRef} type="text" inputMode="numeric" value={draft} aria-label="Target hires per month" onChange={event => setDraft(event.target.value)} onBlur={commitTarget} onKeyDown={event => {
          if (event.key === 'Enter') {
            commitTarget();
          }
          if (event.key === 'Escape') {
            setIsEditingTarget(false);
          }
        }} className="w-[5ch] bg-transparent border-b-2 border-[#7FE0C3] text-center text-8xl md:text-9xl font-bold text-white tracking-tight outline-none" data-id="element-221" data-mp="kk2iiu1l0py7" />
            <span className="text-3xl md:text-4xl font-semibold text-[#7FE0C3]" data-id="element-222" data-mp="pj8iv15kvkng">
              hires / month
            </span>
            {increaseButton}
          </div> : <div className="flex items-center justify-center gap-4" data-id="element-223" data-mp="13gx1e2c6afpc">
            {decreaseButton}
            <button type="button" onClick={startEditingTarget} aria-label={`Average monthly output, ${rangeLow} to ${rangeHigh} hires. Click to enter a target.`} className="flex items-baseline gap-3 rounded-lg px-2 border-b-2 border-dashed border-[#7FE0C3]/40 hover:border-[#7FE0C3] transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-224" data-mp="yhu10v750xnp">
              <span className="text-8xl md:text-9xl font-bold text-white tracking-tight" data-id="element-225" data-mp="zoi8739ei5d3">
                {rangeLow}–{rangeHigh}
              </span>
              <span className="text-3xl md:text-4xl font-semibold text-[#7FE0C3]" data-id="element-226" data-mp="18nv7j33t9dk3">
                hires / month
              </span>
            </button>
            {increaseButton}
          </div>}

        <p className="mt-4 text-sm text-slate-500" data-id="element-227" data-mp="2k3aff1xk5ptz">
          Click any number to set it, or use the arrows to move in $2,500 steps.
        </p>

        <div className="mt-12 pt-9 border-t border-[#7FE0C3]/20 grid grid-cols-1 sm:grid-cols-2 gap-9 text-left" data-id="element-228" data-mp="2p0twsapjyiq5">
          <div data-id="element-229" data-mp="de010dmu9ezt">
            <div className="text-sm font-bold uppercase tracking-widest text-[#7FE0C3] mb-2" data-id="element-230" data-mp="dgma4hpdo2k5">
              Recommended Monthly Ad Spend
            </div>
            <div className="text-4xl font-bold text-white" data-id="element-231" data-mp="3nx25wt1d6tk">
              <EditableNumber value={adSpend} onChange={onAdSpendChange} min={500} max={250000} label="Recommended monthly ad spend" prefix="$" className="text-4xl font-bold text-white" data-id="element-232" data-mp="3uvj9bzeyrrf6" />
              <span className="ml-2 text-lg font-medium text-slate-500" data-id="element-233" data-mp="3guyqx4cynxm7">
                / month
              </span>
            </div>
            <p className="mt-2 text-base text-white" data-id="element-234" data-mp="3bxyiwnwjwksg">
              (separate from monthly service)
            </p>
          </div>

          <div className="sm:text-right" data-id="element-235" data-mp="1f1rbluxclg0b">
            <div className="text-sm font-bold uppercase tracking-widest text-[#7FE0C3] mb-2" data-id="element-236" data-mp="1a4sgpa5v1512">
              Cost Per Hire Range
            </div>
            <div className="text-4xl font-bold text-white" data-id="element-237" data-mp="34j63ye5rp3sc">
              <EditableNumber value={cpaLow} onChange={next => onCpaLowChange(Math.min(next, cpaHigh - 25))} min={25} max={cpaHigh - 25} label="Low end cost per hire" prefix="$" className="text-4xl font-bold text-white" data-id="element-238" data-mp="39d0t3x456ayt" />
              <span className="mx-2 text-slate-500" data-id="element-239" data-mp="3n9xh7qsvlec0">–</span>
              <EditableNumber value={cpaHigh} onChange={next => onCpaHighChange(Math.max(next, cpaLow + 25))} min={cpaLow + 25} max={5000} label="High end cost per hire" prefix="$" className="text-4xl font-bold text-white" data-id="element-240" data-mp="3sabi8ow27o0j" />
            </div>
            <p className="mt-2 text-base text-slate-500" data-id="element-241" data-mp="26xqwijiplh8d">
              Typed hire targets use the ${fmt(Math.round(midCpa))} midpoint.
            </p>
          </div>
        </div>

        <div className="mt-10 pt-7 border-t border-[#7FE0C3]/20" data-id="element-242" data-mp="1uf4zi3bic7fo">
          <p className="rounded-xl bg-[#7FE0C3]/10 border border-[#7FE0C3]/30 px-6 py-4 text-[#7FE0C3] text-xl md:text-2xl font-semibold leading-snug" data-id="element-243" data-mp="1ugzb2r0w2n73">
            Ad spend is paid directly to the job platforms and is separate from
            HIRE's fees.
          </p>
        </div>
      </div>
    </div>;
}